import {useCallback, useEffect, useMemo, useRef} from 'react'
import useCachedProducts from './use-cached-products'
import useAsync from '../commerce-api/hooks/useAsync'
import {useCommerceAPI} from '../commerce-api/contexts'
import CustomRecommendations from '../commerce-api/custom-recommendations'

/**
 * Hook to get recommendations from Einstein or Custom recommender
 * @param {string} [recommender]
 * @param {string} [zone]
 * @param {number} [maxRecommendations] - Maximum number of recommendations to fetch
 * @param {object} [productParameters] - Parameters to pass to the product API
 * @return {{
 *   isAvailable: boolean,
 *   isLoading: boolean,
 *   recommendations: Object,
 *   getRecommendations: Function,
 * }}
 */
const useRecommendations = (
    {recommender, zone},
    {maxRecommendations, productParameters = {allImages: true}} = {}
) => {
    const api = useCommerceAPI()
    const getCachedProducts = useCachedProducts()
    // flag if the recommender is custom
    const isCustom = useMemo(
        () => (zone ? false : recommender.startsWith('custom')),
        [recommender, zone]
    )
    // flag if einstein is available
    const isAvailable = useMemo(() => Boolean(api.einstein), [api])
    // get the recommender API based on the type
    const recommenderApi = useMemo(
        () => (isCustom ? new CustomRecommendations(api) : api.einstein),
        [api, isCustom]
    )
    // store the active recommendations request key to prevent multiple calls for the same data and to skip processing the response if the request has changed
    const activeRecommendationsRequest = useRef(null)
    // async hook to load recommendations
    const {
        execute: loadRecommendations,
        loading: recommendationsLoading,
        data: loadedRecommendations,
        error: recommendationsError,
        reset: resetRecommendations
    } = useAsync((method, ...args) => recommenderApi[method](...args))
    // async hook to load product details
    const {
        execute: loadProducts,
        loading: productsLoading,
        data: loadedProducts,
        error: productsError,
        reset: resetProducts
    } = useAsync(getCachedProducts)

    // combined isLoading state
    const isLoading = useMemo(
        () => recommendationsLoading || productsLoading,
        [productsLoading, recommendationsLoading]
    )

    // notify about recommendations error
    useEffect(() => {
        if (recommendationsError) {
            console.error('Error fetching recommendations', recommendationsError)
        }
    }, [recommendationsError])
    // notify about products error
    useEffect(() => {
        if (productsError) {
            console.error('Error fetching product data for recommendations', productsError)
        }
    }, [productsError])

    // fetch product details for the recommendations
    const fetchRecProductDetails = useCallback(
        (reco) => {
            const ids = reco?.recs?.map((rec) => rec.id)

            // limit recommendations based on the max recommendations parameter
            if (maxRecommendations > 0 && ids?.length > maxRecommendations) {
                ids.length = maxRecommendations
            }

            if (ids?.length > 0) {
                loadProducts({
                    parameters: {
                        ...productParameters,
                        ids: ids.join(',')
                    }
                })
            }
        },
        [loadProducts, maxRecommendations, productParameters]
    )

    // combine the recommendations with the product details
    const recommendations = useMemo(() => {
        if (!loadedRecommendations || !loadedProducts) {
            return null
        }

        // Merge the product detail into the recommendations response
        return {
            ...loadedRecommendations,
            recs:
                loadedRecommendations.recs
                    ?.map((rec) => {
                        const product = loadedProducts.data?.find(
                            (product) => product?.id === rec?.id
                        )
                        // skip if the product is not found - might be due to offline product or limit of max recommendations
                        if (!product) {
                            return null
                        }
                        return {
                            ...rec,
                            ...product,
                            productId: rec.id,
                            image: {disBaseLink: rec.imageUrl, alt: rec.productName}
                        }
                    })
                    // filter out the null values
                    .filter(Boolean) || []
        }
    }, [loadedRecommendations, loadedProducts])

    const getRecommendations = useCallback(
        async (products, args) => {
            // If no zone or recommender is provided, reset the recommendations and products and return
            if (!zone && !recommender) {
                resetRecommendations()
                resetProducts()
                return
            }

            // prepare the arguments for the API call - this is used to prevent multiple calls for the same data
            let apiArgs
            if (zone) {
                apiArgs = [
                    'getRawZoneRecommendations',
                    recommenderApi.prepareRawZoneRecommendations(zone, products, args)
                ]
            } else if (isCustom) {
                apiArgs = [
                    `get${recommender.split('-')[1]}`,
                    recommenderApi[`prepare${recommender.split('-')[1]}`](products, args)
                ]
            } else {
                apiArgs = [
                    'getRawRecommendations',
                    recommenderApi.prepareRawRecommendations(recommender, products, args)
                ]
            }

            // generate a key for the request
            const requestKey = JSON.stringify(apiArgs)
            // if request with the same arguments is already in progress, skip
            if (activeRecommendationsRequest.current === requestKey) {
                // skip if the request is already in progress
                return
            }

            // reset the recommendations and products as we'll be loading new ones
            resetRecommendations()
            resetProducts()
            // set the current request key
            activeRecommendationsRequest.current = requestKey

            // load the recommendations
            const recommendations = await loadRecommendations(...apiArgs)

            // if the request key has changed, skip processing the response as this is no longer the active request
            if (activeRecommendationsRequest.current !== requestKey) {
                return
            }

            // enrich the recommendations with product details - the result will be stored in the loadedProducts state
            // instead of using effect we directly call the second step to prevent a render cycle that would report not loading but no data as well
            fetchRecProductDetails(recommendations)
        },
        [
            fetchRecProductDetails,
            isCustom,
            loadRecommendations,
            recommender,
            recommenderApi,
            resetProducts,
            resetRecommendations,
            zone
        ]
    )

    return {
        isAvailable,
        isCustom,
        isLoading,
        recommendations,
        getRecommendations
    }
}

export default useRecommendations
